import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { PermisosUsuario, ModuloSistema } from '@suminuta/shared';

// =====================================================
// QUERY KEYS
// =====================================================

export const permisosUsuarioKeys = {
  all: ['permisos-usuario'] as const,
  usuario: (usuarioId: number) =>
    [...permisosUsuarioKeys.all, usuarioId] as const,
  verificar: (usuarioId: number, modulo: string, permiso: string) =>
    [
      ...permisosUsuarioKeys.all,
      'verificar',
      usuarioId,
      modulo,
      permiso,
    ] as const,
  modulosAccesibles: (usuarioId: number) =>
    [...permisosUsuarioKeys.all, 'modulos-accesibles', usuarioId] as const,
};

// =====================================================
// QUERIES
// =====================================================

/**
 * Hook para obtener todos los permisos de un usuario
 */
export const usePermisosUsuario = (usuarioId: number) => {
  return useQuery({
    queryKey: permisosUsuarioKeys.usuario(usuarioId),
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: PermisosUsuario;
      }>(`/roles-permisos/usuarios/${usuarioId}/permisos`);
      return response.data.data;
    },
    enabled: !!usuarioId,
  });
};

/**
 * Hook para verificar si un usuario tiene un permiso específico
 */
export const useVerificarPermiso = (
  usuarioId: number,
  modulo: string,
  permiso: string
) => {
  return useQuery({
    queryKey: permisosUsuarioKeys.verificar(usuarioId, modulo, permiso),
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: { tienePermiso: boolean };
      }>(
        `/roles-permisos/usuarios/${usuarioId}/permisos/verificar?modulo=${modulo}&permiso=${permiso}`
      );
      return response.data.data.tienePermiso;
    },
    enabled: !!usuarioId && !!modulo && !!permiso,
  });
};

/**
 * Hook para obtener módulos accesibles por un usuario
 */
export const useModulosAccesibles = (usuarioId: number) => {
  return useQuery({
    queryKey: permisosUsuarioKeys.modulosAccesibles(usuarioId),
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: ModuloSistema[];
      }>(`/roles-permisos/usuarios/${usuarioId}/modulos-accesibles`);
      return response.data.data;
    },
    enabled: !!usuarioId,
  });
};
